import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import {
    getTaxRules,
    createTaxRule,
    updateTaxRule,
    deleteTaxRule
} from "../../../../../redux/actions/SocietyPortal/TaxRuleAction/TaxRuleAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import Breadcrumb from "../../../../../components/Breadcrumb";
import TaxRuleModal from "./TaxRuleModal";

const TaxRules = () => {
    const dispatch = useDispatch();
    const { taxRulesData, loading, createLoading, updateLoading, deleteLoading } = useSelector(state => state.TaxRuleReducer);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        rule_name: "",
        code: "",
        code_type: "SAC",
        description: "",
        rate_cgst: "",
        rate_sgst: "",
        rate_igst: "",
        threshold_limit: "",
        threshold_logic: "EXEMPT_BELOW"
    });
    const [editId, setEditId] = useState(null);
    const [viewMode, setViewMode] = useState("list"); // Default to list for clarity

    // Get society_id from localStorage
    const societyId = selectedSocietyStorer.getSocietyId();

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            dispatch(getTaxRules({ society_id: societyId }));
        }
    }, [dispatch, societyId, refreshKey]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation handled by required attributes, extra checks can be added here

        const payload = {
            ...formData,
            rate_cgst: (parseFloat(formData.rate_cgst) || 0).toFixed(2),
            rate_sgst: (parseFloat(formData.rate_sgst) || 0).toFixed(2),
            rate_igst: (parseFloat(formData.rate_igst) || 0).toFixed(2),
            threshold_limit: (parseFloat(formData.threshold_limit) || 0).toFixed(2),
            society_id: Number(societyId), // Ensure society_id is sent as number
            onSuccess: () => {
                handleCloseModal();
                dispatch(getTaxRules({ society_id: societyId }));
            }
        };

        if (editId) {
            dispatch(updateTaxRule({ id: editId, ...payload }));
        } else {
            dispatch(createTaxRule(payload));
        }
    };

    const handleEdit = (item) => {
        setFormData({
            rule_name: item.rule_name,
            code: item.code,
            code_type: item.code_type,
            description: item.description,
            rate_cgst: item.rate_cgst,
            rate_sgst: item.rate_sgst,
            rate_igst: item.rate_igst,
            threshold_limit: item.threshold_limit,
            threshold_logic: item.threshold_logic
        });
        setEditId(item.tax_rule_id);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this tax rule?")) {
            dispatch(deleteTaxRule({
                id,
                onSuccess: () => {
                    dispatch(getTaxRules({ society_id: societyId }));
                }
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            rule_name: "",
            code: "",
            code_type: "SAC",
            description: "",
            rate_cgst: "",
            rate_sgst: "",
            rate_igst: "",
            threshold_limit: "",
            threshold_logic: "EXEMPT_BELOW"
        });
        setEditId(null);
    };

    // Separate Default and Society Specific Rules
    // API returns all mixed. We check for society_id property.
    // Assuming backend returns { society_id: null } for defaults.
    // Separate Default and Society Specific Rules
    // API returns all mixed. We check for society_id property.
    // Assuming backend returns { society_id: null } for defaults.
    const defaultRules = taxRulesData?.filter(r => !r.society_id) || [];
    const customRules = taxRulesData?.filter(r => r.society_id === societyId) || [];

    return (
        <div className="invoice-setup-page">
            {/* Breadcrumb */}
            <Breadcrumb
                headingTitle="Tax Rules"
                title="Invoice Setup" // Or appropriate parent crumb
                description="Manage default and society-specific tax rules"
                links={[{ text: 'Tax Rules', to: '#' }]} // Adjust path if needed
            />

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:percent" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> System default rules are available for all societies. You can create custom rules for specific needs.
                    </p>
                </div>
            </div>

            {/* Actions */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <button
                    onClick={() => setShowModal(true)}
                    className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                    style={{ backgroundColor: '#f59e0b' }}
                    disabled={loading}
                >
                    <Icon icon="mdi:plus-circle" className="text-base" />
                    Create Custom Rule
                </button>

                 <div className="btn-group" role="group">
                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-2 px-16 py-10 ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('list')}
                    >
                        <Icon icon="mdi:view-list" className="text-lg" />
                        <span className="d-none d-sm-inline">List</span>
                    </button>
                    {/* Card view can be added if needed */}
                </div>
            </div>

            {/* Custom Rules Section */}
            <div className="mb-16">
                <h6 className="fw-semibold mb-16">Society Specific Rules</h6>
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Code (Type)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">CGST (%)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">SGST (%)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">IGST (%)</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customRules.length > 0 ? customRules.map((item, index) => (
                                        <tr key={item.tax_rule_id || index}>
                                            <td className="ps-12 py-8 align-middle">
                                                <div className="fw-medium text-primary-600">{item.rule_name}</div>
                                                <div className="text-xs text-secondary-light">{item.description}</div>
                                            </td>
                                            <td className="py-16 align-middle">
                                                <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">
                                                    {item.code} ({item.code_type})
                                                </span>
                                            </td>
                                            <td className="py-16 align-middle fw-medium">{item.rate_cgst}%</td>
                                            <td className="py-16 align-middle fw-medium">{item.rate_sgst}%</td>
                                            <td className="py-16 align-middle fw-medium text-success-600">
                                                {item.rate_igst}%
                                            </td>
                                            <td className="pe-12 py-8 align-middle text-end">
                                                <div className="d-flex align-items-center justify-content-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                    >
                                                        <Icon icon="mdi:pencil" className="text-lg" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.tax_rule_id)}
                                                        className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-12 py-6 radius-6"
                                                        disabled={deleteLoading}
                                                    >
                                                        <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-20 text-secondary-light">
                                                No society-specific rules found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Default Rules Section */}
            <div className="mb-16">
                <h6 className="fw-semibold mb-16 text-secondary">System Default Rules</h6>
                <div className="card radius-12 border-0 shadow-sm opacity-75">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">Name</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Code (Type)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">CGST (%)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">SGST (%)</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">IGST (%)</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {defaultRules.length > 0 ? defaultRules.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td className="ps-12 py-8 align-middle">
                                                <div className="fw-medium text-secondary-dark">{item.rule_name}</div>
                                                <div className="text-xs text-secondary-light">{item.description}</div>
                                            </td>
                                            <td className="py-16 align-middle">
                                                <span className="badge bg-neutral-100 text-secondary-light fw-medium px-12 py-6 radius-4">
                                                    {item.code} ({item.code_type})
                                                </span>
                                            </td>
                                            <td className="py-16 align-middle fw-medium">{item.rate_cgst}%</td>
                                            <td className="py-16 align-middle fw-medium">{item.rate_sgst}%</td>
                                            <td className="py-16 align-middle fw-medium text-success-600">
                                                {item.rate_igst}%
                                            </td>
                                            <td className="pe-12 py-8 align-middle text-end">
                                                <span className="badge bg-neutral-200 text-secondary-light">System Default</span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-20 text-secondary-light">
                                                No default rules found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <TaxRuleModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                loading={createLoading || updateLoading}
            />
        </div>
    );
};

export default TaxRules;
